import { Option } from '../types/common/option';
import { AvailabilityDetails, Listing } from '../types/listing/listing';
import { AvailabilityDetailsConfig, Config } from '../types/config/config';

const MONTH_MAP: { [k: string]: number } = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12
};

function parseMonthToNumber(monthName: string): number | null {
    const lower = monthName.toLowerCase().trim();
    if (MONTH_MAP[lower]) {
        return MONTH_MAP[lower];
    }
    const parsed = parseInt(monthName, 10);
    return !isNaN(parsed) ? parsed : null;
}

function parseQuarterEndMonth(quarterName: string): number | null {
    const qNum = parseInt((quarterName + '').replace(/[^0-9]/g, ''), 10);
    if (!isNaN(qNum) && qNum >= 1 && qNum <= 4) {
        return qNum * 3;
    }
    return null;
}

export function generateYearOptions(yearRange: { back: number, next: number }): Option[] {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - yearRange.back;
    const endYear = currentYear + yearRange.next;
    const yearOptions: Option[] = [];

    for (let year = startYear; year <= endYear; year++) {
        yearOptions.push({
            label: year.toString(),
            value: year.toString(),
            order: year - startYear + 1
        });
    }

    return yearOptions;
}

export function normalizeAvailabilityDetails(
    previous: AvailabilityDetails | undefined,
    changes: any,
    config: Pick<AvailabilityDetailsConfig, 'children'>
): AvailabilityDetails {
    const current = previous || {};
    const availabilityDetails: AvailabilityDetails = { ...current, ...changes };
    const children = Array.isArray(config.children) ? config.children : [];
    const hasShortTerm = children.some((child) => child.show && child.name === 'shortTerm');
    const hasOnRequest = children.some((child) => child.show && child.name === 'onRequest');
    const hasMonthsAfterContract = children.some((child) => child.show && child.name === 'monthsAfterContract');

    if (hasOnRequest && hasShortTerm && changes.onRequest !== !!current.onRequest && changes.onRequest) {
        availabilityDetails.shortTerm = false;
    } else if (hasShortTerm && hasOnRequest && changes.shortTerm !== !!current.shortTerm && changes.shortTerm) {
        availabilityDetails.onRequest = false;
    }

    if (hasMonthsAfterContract) {
        const monthsAfterContract: any = availabilityDetails.monthsAfterContract;
        availabilityDetails.monthsAfterContract = (monthsAfterContract === '' || monthsAfterContract === undefined)
            ? null
            : (monthsAfterContract !== null ? Number(monthsAfterContract) : null);
    }

    return availabilityDetails;
}

export function isAvailabilityDetailsPast(year: string, monthName?: string, quarterName?: string): boolean {
    if (!year || year === '') {
        return false;
    }

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const numYear = parseInt(year, 10);

    if (isNaN(numYear)) {
        return false;
    }

    if (numYear < currentYear) {
        return true;
    }
    if (numYear > currentYear) {
        return false;
    }

    if (monthName && monthName !== '') {
        const numMonth = parseMonthToNumber(monthName);
        if (numMonth !== null) {
            return numMonth <= currentMonth;
        }
    }

    if (quarterName && quarterName !== '') {
        const quarterEndMonth = parseQuarterEndMonth(quarterName);
        if (quarterEndMonth !== null) {
            return quarterEndMonth <= currentMonth;
        }
    }
    return true;
}

export function isAvailableFromPast(availableFrom: string | Date): boolean {
    if (!availableFrom) {
        return false;
    }
    return new Date(availableFrom) < new Date();
}

export function getAvailableImmediately(config: Config, listing: Listing): boolean {
    if (!config || !config.availableImmediately || !config.availableImmediately.show) {
        return false;
    }
    if (config.availabilityDetails && config.availabilityDetails.show) {
        const details = listing.availabilityDetails || {};
        return isAvailabilityDetailsPast(details.availableYear || '', details.availableMonth, details.availableQuarter);
    }
    if (config.availableFrom && config.availableFrom.show) {
        const availableFrom = listing.availableFrom;
        return availableFrom ? isAvailableFromPast(availableFrom) : false;
    }
    return false;
}
